/**
 * FANTASY LANDING PAGE
 * 
 * Entry point for Fantasy Football:
 * - Quick Build (default): AI generates options based on your description
 * - Custom Builder: Full control with 5-phase wizard
 */

import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Sparkles, 
  Settings, 
  Zap, 
  Clock, 
  Trophy, 
  Users,
  ChevronRight,
  ArrowRight
} from 'lucide-react'

export default function FantasyLandingPage() {
  const navigate = useNavigate()
  
  return (
    <div className="min-h-screen py-12 relative overflow-hidden">
      {/* Background decorative blobs */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-40 right-20 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-20 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-fuchsia-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-7xl font-black text-white mb-4">
            Fantasy <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-fuchsia-500 to-emerald-400">Football</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Create custom fantasy contests in seconds. AI does the heavy lifting. 🏈
          </p>
        </motion.div>

        {/* Two Options */}
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Quick Build - Primary */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => navigate('/fantasy/quick')}
            className="relative group text-left"
          >
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 rounded-3xl blur-xl opacity-50 group-hover:opacity-100 transition-opacity" />
            
            <div className="relative p-8 rounded-3xl border-2 border-violet-500/30 bg-slate-800/80 backdrop-blur-sm hover:border-violet-500 transition-all group-hover:scale-[1.02]">
              {/* Recommended badge */}
              <div className="absolute -top-3 left-6">
                <span className="px-3 py-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-xs font-bold rounded-full">
                  ⚡ RECOMMENDED
                </span>
              </div>
              
              <div className="flex items-center gap-4 mb-6 mt-2">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Quick Build</h2>
                  <p className="text-violet-400">AI creates it for you</p>
                </div>
              </div>
              
              <p className="text-gray-300 mb-6">
                Describe your league in one sentence. AI generates 3 creative contest options. Pick one and go!
              </p>
              
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="px-3 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-lg text-violet-300 text-sm flex items-center gap-1">
                  <Clock className="w-3 h-3" /> ~30 seconds
                </span>
                <span className="px-3 py-1.5 bg-violet-500/10 border border-violet-500/20 rounded-lg text-violet-300 text-sm flex items-center gap-1">
                  <Zap className="w-3 h-3" /> No expertise needed
                </span>
              </div>
              
              <div className="flex items-center gap-2 text-violet-400 font-semibold group-hover:gap-3 transition-all">
                Start Quick Build
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </motion.button>
          
          {/* Custom Builder - Secondary */}
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => navigate('/fantasy/custom')}
            className="relative group text-left"
          >
            <div className="relative p-8 rounded-3xl border-2 border-emerald-500/30 bg-slate-800/80 backdrop-blur-sm hover:border-emerald-500 transition-all group-hover:scale-[1.02]">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
                  <Settings className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Custom Builder</h2>
                  <p className="text-emerald-400">Full control</p>
                </div>
              </div>
              
              <p className="text-gray-300 mb-6">
                Chat with AI to configure every detail. Games, scoring, roster rules, lock timing, prizes - you decide everything.
              </p>
              
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-300 text-sm flex items-center gap-1">
                  <Clock className="w-3 h-3" /> 5-10 minutes
                </span>
                <span className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-300 text-sm flex items-center gap-1">
                  <Settings className="w-3 h-3" /> Unlimited options
                </span>
              </div>
              
              <div className="flex items-center gap-2 text-emerald-400 font-semibold group-hover:gap-3 transition-all">
                Start Custom Builder
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </motion.button>
        </div>

        {/* Example use cases */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-16 text-center"
        >
          <h3 className="text-lg font-semibold text-gray-400 mb-6">Perfect for...</h3>
          
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { icon: '🏢', label: 'Office parties' },
              { icon: '👨‍👩‍👧‍👦', label: 'Family games' },
              { icon: '🎓', label: 'Greek life' },
              { icon: '🍺', label: 'Bar leagues' },
              { icon: '👥', label: 'Friend groups' },
              { icon: '🏆', label: 'Serious competition' },
            ].map((item) => (
              <div
                key={item.label}
                className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-xl text-gray-300 flex items-center gap-2"
              >
                <span>{item.icon}</span>
                <span className="text-sm">{item.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Browse existing contests */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-500">
            Or{' '}
            <button
              onClick={() => navigate('/fantasy/contests')}
              className="text-violet-400 hover:text-violet-300 underline"
            >
              browse existing contests
            </button>
            {' '}to join
          </p>
        </motion.div>
      </div>
    </div>
  )
}
